import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Target, Eye, Heart, Users, Lightbulb, Linkedin, ChevronDown } from 'lucide-react';
import HeroSection from '../components/HeroSection';
import AnimatedImage from '../components/AnimatedImage';
import WaveOverlay from '../components/WaveOverlay';
import karenHeadshot from '../assets/images/Karen headshot crop 03.2025.jpg';

// About the Founder Component - Two Column Layout
function AboutTheFounder() {
  return (
    <section id="about-founder" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {/* Karen's Full Bio - Left Column */}
          <div className="relative isolate">
            {/* Background accent */}
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-x-8 -top-8 -z-10 h-48 rounded-3xl bg-gradient-to-br from-slate-100 to-white blur-2xl sm:-inset-x-12 sm:h-64"
            />

            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm sm:p-12">
              <header className="mb-6">
                <p className="text-sm font-medium uppercase tracking-wider text-slate-500">Insight Digital Agency</p>
                <h2 className="mt-2 text-3xl font-semibold leading-tight text-slate-900 sm:text-4xl">
                  About the Founder
                </h2>
              </header>

              <div className="prose prose-slate max-w-none">
                <p>
                  <strong>Karen J. Schwartz</strong> is the founder and strategic architect behind
                  <strong> Insight Digital Agency</strong>. With a career that bridges healthcare,
                  real estate, and digital transformation, she brings a unique combination of logic,
                  empathy, and precision to every client engagement.
                </p>
                <p>
                  Karen's approach is simple but disciplined: diagnose inefficiency, design intelligent
                  systems, and empower teams to operate with clarity and confidence. Her leadership
                  philosophy centers on trust, transparency, and long-term value — qualities that shape
                  both her work and her relationships.
                </p>
                <p>
                  She believes effective transformation isn't about chasing trends; it's about aligning
                  people, processes, and technology around a clear, measurable purpose. Under her
                  guidance, Insight Digital Agency helps business owners move from overwhelm to
                  operational excellence — one smart system at a time.
                </p>
              </div>
            </div>
          </div>

          {/* Nikki's Placeholder - Right Column */}
          <div className="relative isolate">
            <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-8 shadow-sm sm:p-12 h-full flex flex-col items-center justify-center">
              <div className="text-center space-y-4">
                <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold border-2 border-orange-200">
                  🚧 Under Construction 🚧
                </div>
                <h2 className="text-2xl font-semibold text-slate-700">
                  Nikki's Full Bio
                </h2>
                <p className="text-slate-500 text-sm">
                  Coming Soon
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const About = ({ onBookMeeting }) => {
  const sections = [
    {
      icon: <Lightbulb className="w-8 h-8 text-orange-500" />,
      title: "Our Inspiration",
      content: "We are inspired by the potential we see in every small business owner. Your passion, dedication, and entrepreneurial spirit drive us to create digital solutions that truly make a difference. We believe that with the right digital strategy, any business can achieve remarkable growth and success."
    },
    {
      icon: <Target className="w-8 h-8 text-blue-600" />,
      title: "Our Mission",
      content: "Our mission is to empower small and medium businesses with cutting-edge digital marketing strategies that deliver measurable results. We are committed to providing personalized service, innovative solutions, and unwavering support to help our clients achieve their business goals and exceed their expectations."
    },
    {
      icon: <Eye className="w-8 h-8 text-green-600" />,
      title: "Our Vision",
      content: "To be the leading digital marketing agency that transforms how small businesses connect with their customers online. We envision a future where every business, regardless of size, has access to professional-grade digital marketing tools and strategies that drive sustainable growth."
    },
    {
      icon: <Heart className="w-8 h-8 text-red-500" />,
      title: "Our Values",
      content: "Integrity, transparency, and collaboration are at the core of everything we do. We believe in building long-term partnerships with our clients, maintaining open communication, and delivering on our promises. Your success is our success, and we're committed to going above and beyond to achieve it together."
    }
  ];

  const teamMembers = [
    {
      name: "Karen Schwartz",
      role: "Founder",
      bio: "With a career that bridges healthcare, real estate, and digital transformation, Karen brings a unique combination of logic, empathy, and precision to every client engagement.",
      linkedin: "https://www.linkedin.com/in/insightdigitalagency/",
      image: karenHeadshot
    },
    {
      name: "Nikki Schwartz",
      role: "AI, Automation, & Audio Specialist",
      bio: "Building things with AI. AI Tool Builder | AI Consultant & Trainer | AI & Automation | Audio Specialist & Professional Voice Talent",
      linkedin: "https://www.linkedin.com/in/nichalia/",
      image: null
    }
  ];



  return (
    <>
      <Helmet>
        <title>About Us | Insight Digital Agency</title>
        <meta name="description" content="Meet the team behind Insight Digital Agency. Founded by Karen J. Schwartz, we help small businesses streamline operations and grow with digital marketing and AI automation." />
        <link rel="canonical" href="https://insightdigitalagency.com/about" />
        <meta property="og:title" content="About Us | Insight Digital Agency" />
        <meta property="og:description" content="Meet the team behind Insight Digital Agency. Founded by Karen J. Schwartz, we help small businesses streamline operations and grow." />
        <meta property="og:url" content="https://insightdigitalagency.com/about" />
      </Helmet>
      <div className="min-h-screen">
      {/* Hero Section */}
      <HeroSection>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 relative z-40">
            <div className="flex items-center space-x-2 text-orange-300">
              <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                Insight Digital Agency
              </span>
              <span>» About</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold">Who We Are</h1>
            <p className="text-lg sm:text-xl text-blue-100 leading-relaxed">
              Insight Digital Agency helps small and midsize businesses streamline operations, modernize processes, and grow with confidence. The agency was founded to solve a common problem: hard-working owners overwhelmed by outdated systems and fragmented digital efforts.
            </p>
            <p className="text-base sm:text-lg text-blue-100 leading-relaxed">
              Founder KJ Schwartz, a seasoned real estate broker and former healthcare professional, understands the complexity of running a business from every angle. After building multiple companies from the ground up, she saw firsthand how clarity, automation, and strategic design can transform daily operations and long-term profitability.
            </p>
            <p className="text-base sm:text-lg text-blue-100 leading-relaxed">
              Today, Insight Digital Agency applies that experience to every client engagement. The team combines practical business insight with modern technology to create efficient systems that save time, reduce errors, and enhance performance. From process audits and automation to AI integration and clean digital presence design, the agency's mission is simple: to help clients work with informed intention so the business supports their life, not the other way around.
            </p>
          </div>
          <AnimatedImage type="rotating-box">
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Digital Innovation</h3>
              <p className="text-blue-100">Connecting businesses with their customers</p>
            </div>
          </AnimatedImage>
        </div>
      </HeroSection>

      {/* Mission, Vision, Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {sections.map((section, index) => (
              <div key={index} className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                    {section.icon}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                </div>
                <p className="text-gray-600 leading-relaxed">{section.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Our diverse team of digital marketing experts is passionate about helping your business succeed online.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-8 max-w-4xl mx-auto">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 w-full sm:w-80">
                {member.image ? (
                  <div className="h-64 bg-gradient-to-br from-[#1074ad] to-[#163760] relative overflow-hidden">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="h-full w-full object-cover object-center"
                      style={{ 
                        objectPosition: 'center 68%',
                        maskImage: 'radial-gradient(ellipse 70% 80% at 50% 45%, black 60%, transparent 100%)',
                        WebkitMaskImage: 'radial-gradient(ellipse 70% 80% at 50% 45%, black 60%, transparent 100%)'
                      }}
                    />
                  </div>
                ) : (
                  <div className="h-64 bg-gradient-to-br from-[#1074ad] to-[#163760]"></div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                  <p className="text-blue-600 font-semibold mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm mb-4">{member.bio}</p>
                  <div className="flex flex-col gap-2">
                    {member.name === "Karen Schwartz" && (
                      <a 
                        href="#about-founder"
                        className="inline-flex items-center text-orange-600 hover:text-orange-800 transition-colors text-sm font-medium"
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById('about-founder')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        <ChevronDown size={16} className="mr-2" />
                        Read More About Karen
                      </a>
                    )}
                    {member.linkedin && (
                      <a 
                        href={member.linkedin} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors text-sm font-medium"
                      >
                        <Linkedin size={16} className="mr-2" />
                        Connect on LinkedIn
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About the Founder Section */}
      <AboutTheFounder />

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-br from-[#163760] to-[#1074ad] text-white relative">
        <WaveOverlay inverted={true} color="fill-white" />
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">Ready to Transform Your Online Presence?</h2>
          <p className="text-lg sm:text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Let's work together to create a digital marketing strategy that drives real results for your business.
          </p>
          <Button 
            onClick={onBookMeeting}
            className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 text-lg font-semibold rounded-lg"
          >
            Book a Meeting
          </Button>
        </div>
        <WaveOverlay />
      </section>
    </div>
    </>
  );
};

export default About;

